import * as React from 'react';

import * as api from '@gitbook/api-client';
import type * as analytics from '@gitbook/app-analytics/client';
import type {
    NavigationGitSyncSideSheet,
    NavigationScreenSpaceState,
} from '@gitbook/app-navigation';
import { ModalAnimateLifecycle } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Heading,
    SidePanel,
    SidePanelBody,
    SidePanelBodyContent,
    SidePanelCloseButton,
    SidePanelHeader,
    SidePanelHeaderActions,
    SidePanelHeaderMain,
    SidePanelHeaderTitle,
} from '@gitbook/spine-react';

import {
    NavigationRedirect,
    useNavigation,
    useSetAnalyticsScreen,
    useSideSheetNavigation,
} from '../../navigation';
import { apiClient } from '../../state';
import { GitSyncProviderName } from '../GitSyncProviders';
import { HiveBranchHistory } from '../History';
import { GitSyncInstallationStatus } from './GitSyncInstallationStatus';
import { GitSyncLegacyUpgradeAlert } from './GitSyncLegacyUpgradeAlert';
import { GitSyncLegacyUpgradeModal } from './GitSyncLegacyUpgradeModal';

/**
 * SidePanel to show the status of a git sync and its history.
 */
export function GitSyncSidePanel(props: {
    space: api.Space;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
    screen: NavigationScreenSpaceState;
}): React.ReactElement {
    const { space, analyticsContext, screen } = props;
    const { navigate } = useNavigation();

    const revision = !screen.changeRequest && screen.revision ? screen.revision : undefined;
    const hasExistingInstallation = Boolean(space.gitSync?.installationProvider);
    const { opened, selectedTab } = useSideSheetNavigation<NavigationGitSyncSideSheet>(
        'gitsync',
        'status'
    );
    const onClose = React.useCallback(
        () => navigate({ screen: { sideSheet: undefined, revision: undefined, mode: undefined } }),
        [navigate]
    );

    useSetAnalyticsScreen(
        opened
            ? { ...analyticsContext, screen: 'space_setup_gitsync', screenTab: selectedTab }
            : null,
        [selectedTab]
    );

    if (!hasExistingInstallation) {
        return (
            <NavigationRedirect
                to={{
                    current: {
                        sideSheet: undefined,
                    },
                }}
            />
        );
    }
    const providerName = space.gitSync?.installationProvider
        ? GitSyncProviderName[space.gitSync.installationProvider]
        : 'Git';

    return (
        <SidePanel onClose={onClose}>
            <SidePanelHeader>
                <SidePanelHeaderMain>
                    <SidePanelHeaderTitle>
                        <Heading>{`${providerName} sync`}</Heading>
                    </SidePanelHeaderTitle>
                    <SidePanelHeaderActions>
                        {space.gitSync?.url ? (
                            <Button
                                kind="blank"
                                label="Repository"
                                leadingIcon={Icon.Link}
                                iconOnly
                                target="_blank"
                                href={space.gitSync.url}
                            />
                        ) : null}
                        <Button
                            kind="blank"
                            label="Configure sync"
                            leadingIcon={Icon.Settings}
                            iconOnly
                            onPress={() => {
                                if (space.gitSync?.integration) {
                                    navigate({
                                        current: {
                                            sideSheet: {
                                                type: 'integrations',
                                                view: {
                                                    type: 'integration',
                                                    integration: space.gitSync.integration,
                                                },
                                            },
                                        },
                                    });
                                    return;
                                }

                                navigate({
                                    current: {
                                        sideSheet: {
                                            type: 'integrations',
                                            view: {
                                                type: 'category',
                                                category: api.IntegrationCategory.Gitsync,
                                            },
                                        },
                                    },
                                });
                            }}
                        />
                        <SidePanelCloseButton />
                    </SidePanelHeaderActions>
                </SidePanelHeaderMain>
            </SidePanelHeader>
            <SidePanelBody>
                <GitSyncSidePanelContent space={space} revision={revision} />
            </SidePanelBody>
        </SidePanel>
    );
}

/**
 * View Git Sync status and history either through the Git Sync installation
 * or the Git Sync integration.
 */
function GitSyncSidePanelContent(props: {
    space: api.Space;
    revision?: string;
}): React.ReactElement {
    const { space, revision } = props;
    const { navigate } = useNavigation();
    const isLegacyInstallation = Boolean(
        space.gitSync &&
            (space.gitSync.installationProvider === api.GitSyncProvider.GithubLegacy ||
                !space.gitSync?.integration)
    );

    const [modal, setModal] = React.useState<null | 'upgrade' | 'progress'>(null);

    const doUpgrade = async () => {
        await apiClient.spaceGit.deleteLegacyGitInstallation(space.id);

        setModal(null);
        navigate({
            current: {
                space: space.id,
                organization: space.organization,
                sideSheet: {
                    type: 'integrations',
                    view: { type: 'category', category: api.IntegrationCategory.Gitsync },
                },
            },
        });
    };

    return (
        <>
            <ModalAnimateLifecycle>
                {space.gitSync?.repoName && isLegacyInstallation && modal === 'upgrade' ? (
                    <GitSyncLegacyUpgradeModal
                        repository={space.gitSync.repoName}
                        onClose={() => setModal(null)}
                        onUpgrade={doUpgrade}
                    />
                ) : null}
            </ModalAnimateLifecycle>

            <div className="flex flex-col">
                <SidePanelBodyContent>
                    {space.gitSync?.repoName && isLegacyInstallation ? (
                        <div className="mx-6 mt-6 flex">
                            <GitSyncLegacyUpgradeAlert
                                repository={space.gitSync.repoName}
                                startUpgrade={() => setModal('upgrade')}
                            />
                        </div>
                    ) : null}
                    <GitSyncInstallationStatus
                        space={space}
                        isLegacyInstallation={isLegacyInstallation}
                    />
                </SidePanelBodyContent>

                <div className="flex">
                    <HiveBranchHistory
                        space={space}
                        changeRequest={null}
                        initialRevisionId={space.revision}
                        activeRevisionId={revision}
                        filter={{ git: true }}
                        readOnly
                    />
                </div>
            </div>
        </>
    );
}
